import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, StatCard, Button, Badge, getStatusBadge } from '@/backoffice/components/ui';
import { formatCurrency, formatRelativeTime } from '@/backoffice/utils';
import {
  fetchClientDashboard,
  type ClientDashboardData,
  type ClientOrderSummary,
  type CommunityTrip,
} from '@/domains/client/services/client.service';
import {
  Package,
  MapPin,
  Truck,
  Wallet,
  Users,
  History,
  Clock,
  TrendingUp,
  ArrowRight,
  Plus,
  Loader2,
} from 'lucide-react';

export function ClientDashboard() {
  const [data, setData] = useState<ClientDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClientDashboard()
      .then(setData)
      .catch(() => setError('Impossible de charger le tableau de bord.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout userType="client" title="Tableau de bord">
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout userType="client" title="Tableau de bord">
        <Card className="text-center py-12">
          <p className="text-gray-500">{error ?? 'Données indisponibles.'}</p>
        </Card>
      </DashboardLayout>
    );
  }

  const { stats, pending_orders, active_orders, recent_history, community_trips } = data;

  return (
    <DashboardLayout userType="client" title="Tableau de bord">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Link
          to="/dashboard/client/commandes/nouvelle"
          className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-xl shadow-lg shadow-teal-500/20 hover:shadow-xl hover:shadow-teal-500/30 transition-all duration-300"
        >
          <Plus className="w-6 h-6 mb-2" />
          <span className="text-sm font-medium">Nouvelle course</span>
        </Link>
        <Link
          to="/dashboard/client/voisins"
          className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-300"
        >
          <Users className="w-6 h-6 mb-2 text-emerald-600" />
          <span className="text-sm font-medium text-gray-700">Voisins</span>
          {community_trips.length > 0 && (
            <span className="mt-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
              {community_trips.length} trajets
            </span>
          )}
        </Link>
        <Link
          to="/dashboard/client/suivi"
          className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-200 hover:border-teal-300 hover:bg-teal-50 transition-all duration-300"
        >
          <MapPin className="w-6 h-6 mb-2 text-teal-600" />
          <span className="text-sm font-medium text-gray-700">Suivi en direct</span>
        </Link>
        <Link
          to="/dashboard/client/portefeuille"
          className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-all duration-300"
        >
          <Wallet className="w-6 h-6 mb-2 text-amber-600" />
          <span className="text-sm font-medium text-gray-700">Portefeuille</span>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Commandes ce mois"
          value={stats.total_orders_this_month}
          icon={<Package className="w-6 h-6 text-teal-600" />}
          iconBg="bg-blue-50"
        />
        <StatCard
          title="En cours"
          value={stats.active_deliveries}
          icon={<Truck className="w-6 h-6 text-amber-600" />}
          iconBg="bg-amber-50"
        />
        <StatCard
          title="Depenses totales"
          value={formatCurrency(stats.total_spent)}
          icon={<Wallet className="w-6 h-6 text-emerald-600" />}
          iconBg="bg-emerald-50"
        />
        <StatCard
          title="Economies communautaires"
          value={formatCurrency(stats.monthly_savings)}
          icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
          iconBg="bg-purple-50"
        />
      </div>

      {active_orders.length > 0 && (
        <Card className="mb-6" padding="none">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Livraisons en cours</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {active_orders.map((order) => (
              <ActiveOrderRow key={order.id} order={order} />
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="mb-6 lg:mb-0" padding="none">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">En attente</h2>
            <Link to="/dashboard/client/commandes" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
              Voir tout
            </Link>
          </div>
          {pending_orders.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune commande en attente</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pending_orders.map((order) => (
                <PendingOrderRow key={order.id} order={order} />
              ))}
            </div>
          )}
        </Card>

        <Card padding="none">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Trajets de vos voisins
            </h2>
            <Link to="/dashboard/client/voisins" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
              Voir tout
            </Link>
          </div>
          {community_trips.length === 0 ? (
            <div className="p-8 text-center">
              <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun trajet disponible</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {community_trips.map((trip) => (
                <CommunityTripRow key={trip.id} trip={trip} />
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card padding="none">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              Historique recent
            </h2>
            <Link to="/dashboard/client/historique" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
              Voir tout
            </Link>
          </div>
          {recent_history.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Aucune livraison terminee</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recent_history.map((order) => (
                <Link
                  key={order.id}
                  to={`/dashboard/client/commandes/${order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{order.code}</p>
                    <p className="text-sm text-gray-500">
                      {order.departure_zone} <ArrowRight className="w-3 h-3 inline" /> {order.arrival_zone}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(order.price)}</p>
                    <p className="text-sm text-gray-500">{formatRelativeTime(order.created_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}

function ActiveOrderRow({ order }: { order: ClientOrderSummary }) {
  return (
    <Link
      to={`/dashboard/client/commandes/${order.id}`}
      className="block p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-gray-900">{order.code}</span>
        {getStatusBadge(order.status)}
      </div>
      <div className="flex items-start gap-3 text-sm text-gray-600">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="truncate">{order.departure_zone}</span>
        </div>
        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <MapPin className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span className="truncate">{order.arrival_zone}</span>
        </div>
      </div>
      {order.driver_name && (
        <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
          <span className="font-medium">{order.driver_name}</span>
          {order.driver_type === 'community' && (
            <Badge variant="success" size="sm">Voisin</Badge>
          )}
        </p>
      )}
    </Link>
  );
}

function PendingOrderRow({ order }: { order: ClientOrderSummary }) {
  return (
    <Link
      to={`/dashboard/client/commandes/${order.id}`}
      className="block p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-gray-900">{order.code}</span>
        <span className="text-sm text-gray-500">{formatRelativeTime(order.created_at)}</span>
      </div>
      <p className="text-sm text-gray-600 mb-1 line-clamp-1">{order.package_description}</p>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">{order.delivery_type ?? 'Standard'}</span>
        <span className="font-semibold text-gray-900">{formatCurrency(order.price)}</span>
      </div>
    </Link>
  );
}

function CommunityTripRow({ trip }: { trip: CommunityTrip }) {
  return (
    <Link to="/dashboard/client/voisins" className="block p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-gray-900">{trip.driver_name}</span>
        <Badge variant="success" size="sm">{trip.available_space}kg dispo</Badge>
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <span>{trip.from.city}</span>
        <ArrowRight className="w-4 h-4" />
        <span>{trip.to.city}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500 flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {new Date(trip.departure_date).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
          })}
        </span>
        <span className="font-semibold text-emerald-600">
          {formatCurrency(trip.price_per_kg)}/kg
        </span>
      </div>
    </Link>
  );
}
